import { createRouter, createWebHistory } from 'vue-router'
import { useMainStore } from './stores/main'
import { clearInterruptedShownFlag } from './utils/meetingHelpers'

// Login stays eager: it is the landing route, so lazy-loading it would only add a round trip
// before first paint. Every other view is fetched on navigation and kept out of the entry chunk.
import Login from './components/Login'

const routes = [
  { path: '/', name: 'home', component: Login },
  { path: '/login', name: 'login', component: Login },
  { path: '/login/:token', name: 'auto_login', component: () => import('./components/AutoLogin') },
  { path: '/logout', name: 'logout', component: () => import('./components/Logout') },
  { path: '/forgot', name: 'forgot_password', component: () => import('./components/ForgotPassword') },
  { path: '/reset/:token?', name: 'reset_password', component: () => import('./components/ResetPassword') },
  { path: '/activate/:token?', name: 'activate_account', component: () => import('./components/ActivateAccount') },
  { path: '/appointment', name: 'appointment', component: () => import('./components/Appointment') },
  { path: '/meeting/:token?', name: 'meeting', component: () => import('./components/Meeting') },
  { path: '/meeting/:token/cancel', name: 'meeting_cancel', component: () => import('./components/MeetingCancel') },
  { path: '/forced_logout', name: 'forced_logout', component: () => import('./components/ForcedLogout') },
  { path: '/waiting_room', name: 'waiting_room', component: () => import('./components/WaitingRoom') },
  { path: '/profile', name: 'profile', component: () => import('./components/personal/Profile') },
  { path: '/visitor_already_active', name: 'meeting_participant_already_active', component: () => import('./components/MeetingParticipantAlreadyActive'), props: true },
  { path: '/unsupported_browser', name: 'unsupported_browser', component: () => import('./components/UnsupportedBrowser'), props: true },
  { path: '/website_button', name: 'website_button', component: () => import('./components/iframe/WebsiteButton') },
  { path: '/website_button_v2', name: 'website_button_v2', component: () => import('./components/iframe/WebsiteButton_v2') },
  { path: '/plannen', name: 'form_plannen_overview', component: () => import('./components/iframe/WebsiteFormOverview') },
  { path: '/plannen/:site', name: 'form_plannen', component: () => import('./components/iframe/WebsiteForm') },
  { path: '/plannen_v2/:site', name: 'website_agents_selection', component: () => import('./components/iframe/WebsiteFormContainer') },
  { path: '/schedule', name: 'form_schedule_overview', component: () => import('./components/iframe/WebsiteFormOverview') },
  { path: '/schedule/:site', name: 'form_schedule', component: () => import('./components/iframe/WebsiteForm') },
  { path: '/hardwaretest', name: 'hardware_test', component: () => import('./components/HardwareTest') },
  { path: '/zynyo-test', name: 'zynyo_test', component: () => import('./components/ZynyoTest') },
  { path: '/component-lab', name: 'component_lab', component: () => import('./components/internal/ComponentLab') },
  { path: '/error', name: 'error', component: () => import('./components/GenericError') },
  { path: '/:pathMatch(.*)*', name: 'ErrorNotFound', component: () => import('./ErrorNotFound.vue') }
]

/** Routes exposed on the router instance for templates and axios interceptors. */
export const routeGroups = {
  unauthenticated: [
    'home',
    'login',
    'auto_login',
    'forgot_password',
    'reset_password',
    'activate_account',
    'error'
  ],
  authenticated: [
    'logout',
    'forced_logout',
    'appointment',
    'waiting_room',
    'profile',
    'zynyo_test',
    'component_lab'
  ],
  browserCheckExempt: [
    'unsupported_browser',
    'forgot_password',
    'reset_password',
    'website_button',
    'form_plannen',
    'form_schedule'
  ],
  enableNotificationBar: [
    'home',
    'login',
    'auto_login',
    'forgot_password',
    'reset_password'
  ],
  disableReconnectingMessage: [
    'home',
    'login',
    'auto_login',
    'forgot_password',
    'reset_password',
    'unsupported_browser'
  ]
}

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Backward-compatible aliases used in templates and axios.js
router.unauthenticatedRoutes = routeGroups.unauthenticated
router.authenticatedRoutes = routeGroups.authenticated
router.weseedoUnsupportedAllowedRoutes = routeGroups.browserCheckExempt
router.enableNotificationBarRoutes = routeGroups.enableNotificationBar
router.disableReconnectingMessageRoutes = routeGroups.disableReconnectingMessage

const unauthenticatedRoutes = new Set(routeGroups.unauthenticated)
const authenticatedRoutes = new Set(routeGroups.authenticated)
const browserCheckExemptRoutes = new Set(routeGroups.browserCheckExempt)

const SUPPORTED_BROWSERS = ['isChrome', 'isSafari', 'isFirefox', 'isOpera']
const DETECT_RTC_TIMEOUT_MS = 3000

function queriesEqual (a, b) {
  const keysA = Object.keys(a).sort()
  const keysB = Object.keys(b).sort()
  if (keysA.length !== keysB.length) {
    return false
  }
  return keysA.every((key) => String(a[key]) === String(b[key]))
}

function paramsEqual (a, b) {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Query params are route-specific. Strip flags that only belong on login flows
 * when navigating elsewhere so redirects do not carry stale state.
 * Preserve OAuth `state` when `code` is present (Signicat/DigiD return).
 */
function queryForRoute (routeName, query) {
  const nextQuery = { ...query }

  if (routeName !== 'login') {
    delete nextQuery.session_expired
  }
  if (routeName !== 'login' && routeName !== 'auto_login') {
    delete nextQuery.redirect_after_login
    if (!nextQuery.code) {
      delete nextQuery.state
    }
  }

  return nextQuery
}

/**
 * Carry the previous route's query forward. Embed flags (disable_menu, disable_logo,
 * show_device_settings, ...) are set once on /login/:token but read as $route.query on
 * every later route, and a name-only push — which AutoLogin does — drops the query.
 * `to` wins on conflict; queryForRoute still strips the login-only flags.
 */
function buildLocation (routeName, to, from) {
  const mergedQuery = { ...(from ? from.query : null), ...to.query }

  return {
    name: routeName,
    params: to.params,
    query: queryForRoute(routeName, mergedQuery)
  }
}

function isSameLocation (to, location) {
  return (
    to.name === location.name &&
    paramsEqual(to.params, location.params) &&
    queriesEqual(to.query, location.query)
  )
}

/** Proceed, or return a redirect location when normalization or access rules require it. */
function finishNavigation (to, location) {
  if (isSameLocation(to, location)) {
    return true
  }
  return location
}

function redirectTo (routeName, to, from) {
  return finishNavigation(to, buildLocation(routeName, to, from))
}

function hasActiveSession (store) {
  return store.getAccessToken !== null && store.getUser !== false
}

function isLoginEntryRoute (routeName) {
  return unauthenticatedRoutes.has(routeName)
}

function shouldStayOnLoginEntry (store, to) {
  if (!hasActiveSession(store)) {
    return true
  }
  // Stale persisted store after 401: URL flag forces the login screen.
  return to.query.session_expired === 'true'
}

function defaultRouteForSignedInUser (store) {
  return store.getProduct === 'personal' ? 'meeting' : 'appointment'
}

function resolveAgentRoute (store, to) {
  if (!hasActiveSession(store)) {
    return 'login'
  }

  // A meeting-link agent session has no waiting room / appointment list to go back to.
  if (store.isMeetingLinkSession === true && (to.name === 'waiting_room' || to.name === 'appointment')) {
    return 'meeting'
  }

  const lacksAppointmentFeature =
    store.getProduct === 'personal' &&
    !store.userHasFeature('appointment') &&
    (to.name === 'waiting_room' || to.name === 'appointment')

  if (lacksAppointmentFeature) {
    return 'meeting'
  }

  if (to.name === 'profile' && !store.userCanAccessProfile) {
    return 'meeting'
  }

  if (store.getProduct === 'direct' && to.name === 'waiting_room') {
    return 'appointment'
  }

  return null
}

function resolveLoginEntryRoute (store, to) {
  if (shouldStayOnLoginEntry(store, to)) {
    return null
  }
  return defaultRouteForSignedInUser(store)
}

function isWeSeeDoBrowserSupported () {
  const detect = window.DetectRTC || {}
  
  // Native iOS app injects this flag via WKUserScript before page load.
  const isNativeMobileApp = window.is123MeekijkenIOS === true

  if (!detect.isWebRTCSupported && !isNativeMobileApp) {
    return false
  }

  return SUPPORTED_BROWSERS.some((key) => detect.browser?.[key])
}

function waitForDetectRtc () {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      console.warn('[router] DetectRTC.load timed out, continuing navigation')
      resolve()
    }, DETECT_RTC_TIMEOUT_MS)

    const onReady = () => {
      clearTimeout(timeoutId)
      resolve()
    }

    if (typeof window.DetectRTC?.load === 'function') {
      try {
        window.DetectRTC.load(onReady)
      } catch (error) {
        console.warn('[router] DetectRTC.load failed, continuing navigation', error)
        onReady()
      }
      return
    }

    onReady()
  })
}

function clearTransientStoreFlags (store, to, from) {
  if (to.name !== 'meeting') {
    store.setMeetingInterrupted(false)
    store.setMeetingEnded(false)
    store.setMeetingEndedAs(null)
    if (from && from.name === 'meeting') {
      clearInterruptedShownFlag()
    }
  }
  if (to.name !== 'forced_logout' && store.getForcedLogout) {
    store.setForcedLogout(false)
  }
}

function resolveBrowserGuard (to, isSupported) {
  const isMeetingWithoutToken = typeof to.params.token === 'undefined'
  const mustCheckBrowser =
    !browserCheckExemptRoutes.has(to.name) &&
    !isSupported &&
    isMeetingWithoutToken

  if (mustCheckBrowser) {
    return 'unsupported_browser'
  }

  if (to.name === 'unsupported_browser' && isSupported) {
    return 'login'
  }

  return null
}

function resolveAccessRedirect (store, to) {
  if (authenticatedRoutes.has(to.name)) {
    return resolveAgentRoute(store, to)
  }

  if (isLoginEntryRoute(to.name)) {
    return resolveLoginEntryRoute(store, to)
  }

  return null
}

router.beforeEach(async (to, from) => {
  const store = useMainStore()

  await waitForDetectRtc()

  const isSupported = isWeSeeDoBrowserSupported()
  window.isWeSeeDoSupported = isSupported

  clearTransientStoreFlags(store, to, from)

  const browserRedirect = resolveBrowserGuard(to, isSupported)
  if (browserRedirect) {
    return redirectTo(browserRedirect, to, from)
  }

  const accessRedirect = resolveAccessRedirect(store, to)
  if (accessRedirect) {
    return redirectTo(accessRedirect, to, from)
  }

  return redirectTo(to.name, to, from)
})

export default router
